import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { trackSignup, trackToolCompletion } from "@/lib/analytics";

const STORAGE_KEY = "toolkit_lead_captured";

export function getToolkitLeadCaptured(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(STORAGE_KEY) === "true";
}

interface ToolkitLeadCaptureDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
  toolTitle?: string;
}

const ToolkitLeadCaptureDialog = ({
  open,
  onClose,
  onComplete,
  toolTitle,
}: ToolkitLeadCaptureDialogProps) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedName || !trimmedEmail) {
      setError("Please enter your name and email.");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    setSubmitting(true);
    try {
      localStorage.setItem(STORAGE_KEY, "true");
      localStorage.setItem("toolkit_lead_name", trimmedName);
      localStorage.setItem("toolkit_lead_email", trimmedEmail);

      await (supabase as any).from("contact_queries").insert({
        name: trimmedName,
        email: trimmedEmail,
        subject: "Toolkit signup",
        message: toolTitle ? `Used tool (results): ${toolTitle}` : "Used a toolkit tool (results)",
        status: "new",
      });
    } catch (_) {
      // Ignore Supabase errors (e.g. RLS); we've stored in localStorage
    }
    trackSignup("toolkit_lead_capture", toolTitle ?? "toolkit");
    if (toolTitle) trackToolCompletion(toolTitle.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""), toolTitle, "lead_capture");
    setSubmitting(false);
    onComplete();
    onClose();
    // Notify cookie consent to show after toolkit lead is done (so only one dialog at a time)
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("toolkit-lead-captured"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Get more tools & tips</DialogTitle>
          <DialogDescription>
            Leave your name and email to receive coaching resources and updates. We won’t spam you.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="toolkit-lead-name">Name</Label>
            <Input
              id="toolkit-lead-name"
              type="text"
              placeholder="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="toolkit-lead-email">Email</Label>
            <Input
              id="toolkit-lead-email"
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              disabled={submitting}
            />
          </div>
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
          <DialogFooter>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Continue"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ToolkitLeadCaptureDialog;
